import React from "react";
import { Grid, TextField } from "@mui/material";
import { isValidEmail } from "utils/Validation/isValidEmail";
import type { UserDto } from "client/api/UsersApi";
import { SubmittableFormLayout } from "components/form/SubmittableFormLayout";

export interface UserFormProps {
  header: string;
  existingUser?: UserDto;
  onSubmit: (params: UserFormInputs) => Promise<void>;
}
export type UserFormInputs = {
  email: string;
  firstName: string;
  lastName?: string;
};

const emailOptions = {
  required: {
    value: true,
    message: "Please enter an email address for this user.",
  },
  validate: (value: string) => isValidEmail(value) || "Please enter a valid email address.",
  maxLength: {
    value: 255,
    message: "Email address is too long, please enter an email address with less than 255 characters.",
  },
  minLength: {
    value: 4,
    message: "Email address is too short, please enter an email address with at least 4 characters.",
  },
};

const firstNameOptions = {
  required: {
    value: true,
    message: "Please enter a first name for this user.",
  },
  maxLength: {
    value: 255,
    message: "First name too long, please enter a name with less than 255 characters.",
  },
};

const lastNameOptions = {
  maxLength: {
    value: 255,
    message: "Last name too long, please enter a name with less than 255 characters.",
  },
};

export function UserForm(props: UserFormProps) {
  const { existingUser, onSubmit, header } = props;

  return (
    <SubmittableFormLayout
      header={header}
      submitAction={onSubmit}
      forExistingEntity={!!existingUser}
      defaultValues={{
        ...existingUser,
      }}
    >
      {(formController, isEditing) => {
        const { register, formState } = formController;
        return (
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                required={!existingUser} // Only show on adding
                disabled={isEditing && !!existingUser} // Disabled when editing
                error={!!formState?.errors?.email}
                helperText={formState?.errors?.email?.message}
                id="email"
                label="Email Address"
                fullWidth
                variant="standard"
                {...register("email", emailOptions)}
                slotProps={{
                  input: {
                    readOnly: !isEditing,
                  },
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <TextField
                required={isEditing}
                fullWidth
                error={!!formState?.errors?.firstName}
                helperText={formState?.errors?.firstName?.message}
                id="first-name"
                label="First Name"
                variant="standard"
                {...register("firstName", firstNameOptions)}
                slotProps={{
                  input: {
                    readOnly: !isEditing,
                  },
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <TextField
                required={isEditing}
                fullWidth
                error={!!formState?.errors?.lastName}
                helperText={formState?.errors?.lastName?.message}
                id="last-name"
                label="Last Name"
                variant="standard"
                {...register("lastName", lastNameOptions)}
                slotProps={{
                  input: {
                    readOnly: !isEditing,
                  },
                }}
              />
            </Grid>
          </Grid>
        );
      }}
    </SubmittableFormLayout>
  );
}
